// errorReporter — captures unhandled JS errors in the browser and ships
// them to POST /api/v1/logs/client.
//
// Safety contract (see server/docs/SYSTEM_LOGGING_PLAN.md §8):
//   - Never throws. Every public surface is wrapped in try/catch.
//   - Never blocks. Sends are fire-and-forget via raw fetch.
//   - Never fires without a valid auth token.
//   - Strips sensitive keys (password/token/secret/etc.) before sending.
//   - Truncates stack traces to 2000 characters.
//   - Batches up to 5 entries every 15 seconds.
//
// We use raw fetch rather than the axios apiClient for three reasons:
//   1. apiClient unwraps the {data} envelope; the log endpoint returns 202
//      with no envelope.
//   2. apiClient's 401 interceptor would attempt a refresh on log POSTs,
//      adding load to a path that must stay invisible.
//   3. Raw fetch lets us attach a single `.catch(()=>{})` to ensure a
//      failed log POST never becomes another unhandled rejection.

import { storage } from '../utils/storage';

const ENDPOINT = '/api/v1/logs/client';
const FLUSH_INTERVAL_MS = 15_000;
const MAX_PER_FLUSH = 5;
const MAX_QUEUE = 200;
const STACK_LIMIT = 2000;
const TRUNCATED = '[truncated]';

const SENSITIVE = /password|token|secret|authorization|api_key|private_key|card|cvv|pan/i;

type LogModule = 'collections' | 'skip' | 'weighbridge' | 'platform';

interface QueueEntry {
  module: LogModule;
  category: 'client_error';
  message: string;
  context: Record<string, unknown>;
}

interface RawEntry {
  message: string;
  context?: Record<string, unknown>;
}

let initialised = false;
let activeRealm: 'tenant' | 'super' = 'tenant';
const queue: QueueEntry[] = [];
let intervalHandle: ReturnType<typeof setInterval> | null = null;

const COLLECTIONS_PREFIXES = [
  '/collections/dashboard',
  '/collections/diary',
  '/collections/bookings',
  '/collections/receipts',
  '/collections/pending',
  '/collections/invoices',
  '/collections/payment-run',
  '/collections/bacs',
  '/collections/cancellations',
  '/collections/lookup',
];

function moduleForPath(pathname: string): LogModule {
  if (pathname.startsWith('/skip')) return 'skip';
  if (pathname.startsWith('/weighbridge')) return 'weighbridge';
  if (pathname.startsWith('/super')) return 'platform';
  for (const prefix of COLLECTIONS_PREFIXES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return 'collections';
    }
  }
  return 'platform';
}

function sanitiseContext(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (SENSITIVE.test(key)) continue;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = sanitiseContext(value as Record<string, unknown>);
    } else {
      out[key] = value;
    }
  }
  return out;
}

function truncateStack(stack: unknown): string | undefined {
  if (typeof stack !== 'string') return undefined;
  if (stack.length <= STACK_LIMIT) return stack;
  return `${stack.slice(0, STACK_LIMIT)}${TRUNCATED}`;
}

function safeToken(): string | null {
  try {
    // Read the token slot for whichever shell initialised this reporter.
    // The two realms have separate storage slots so a tenant reporter
    // never accidentally posts with a super token (and vice versa).
    return storage.get(
      activeRealm === 'super' ? 'superAccessToken' : 'accessToken',
    );
  } catch {
    return null;
  }
}

function safePush(entry: QueueEntry): void {
  try {
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push(entry);
  } catch {
    // swallow — never throw from a global error path
  }
}

function buildEntry(raw: RawEntry): QueueEntry {
  let pathname = '/';
  try {
    pathname = window.location.pathname;
  } catch {
    // fall through with default
  }
  const ctx = raw.context ?? {};
  const sanitised = sanitiseContext(ctx);
  if ('stack' in sanitised) {
    const truncated = truncateStack(sanitised.stack);
    if (truncated === undefined) {
      delete sanitised.stack;
    } else {
      sanitised.stack = truncated;
    }
  }
  return {
    module: moduleForPath(pathname),
    category: 'client_error',
    message: String(raw.message ?? 'Unknown error').slice(0, 1000),
    context: sanitised,
  };
}

function flush(): void {
  try {
    if (queue.length === 0) return;
    const token = safeToken();
    if (token === null || token === '') {
      // Drop entries — we cannot authenticate the POST.
      queue.length = 0;
      return;
    }
    const batch = queue.splice(0, MAX_PER_FLUSH);
    const body = JSON.stringify({ entries: batch });
    // Fire and forget. A failed fetch must not bubble back into the
    // unhandledrejection handler (that would loop), so we always catch.
    try {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
        keepalive: true,
      }).catch(() => {
        // swallow — silent on POST failure
      });
    } catch {
      // synchronous throw (no fetch in env) — swallow
    }
  } catch {
    // never propagate
  }
}

function handleError(message: string | Event, error?: Error | null): void {
  const ctx: Record<string, unknown> = {
    type: 'onerror',
    url: safeLocation(),
    userAgent: safeUserAgent(),
  };
  if (error !== null && error !== undefined && error.stack !== undefined) {
    ctx.stack = error.stack;
  }
  safePush(buildEntry({ message: messageOf(message, error), context: ctx }));
}

function handleRejection(reason: unknown): void {
  const err = reason instanceof Error ? reason : null;
  const ctx: Record<string, unknown> = {
    type: 'unhandledrejection',
    url: safeLocation(),
    userAgent: safeUserAgent(),
  };
  if (err?.stack !== undefined) ctx.stack = err.stack;
  const msg =
    err?.message ??
    (typeof reason === 'string' ? reason : 'Unhandled rejection');
  safePush(buildEntry({ message: msg, context: ctx }));
}

function messageOf(message: string | Event, error?: Error | null): string {
  if (error?.message !== undefined) return error.message;
  if (typeof message === 'string') return message;
  return 'Unknown error';
}

function safeLocation(): string {
  try {
    return window.location.href;
  } catch {
    return 'unknown';
  }
}

function safeUserAgent(): string {
  try {
    return navigator.userAgent;
  } catch {
    return 'unknown';
  }
}

export interface ErrorReporterOptions {
  /** Which realm's auth token to attach to log POSTs. Defaults to
   *  'tenant' for backwards compatibility with callers that pre-date the
   *  per-realm storage split. */
  realm?: 'tenant' | 'super';
}

export function initErrorReporter(options: ErrorReporterOptions = {}): void {
  try {
    if (initialised) return;
    initialised = true;
    activeRealm = options.realm ?? 'tenant';

    const prior = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      try {
        handleError(message, error ?? null);
      } catch {
        // never throw
      }
      if (typeof prior === 'function') {
        try {
          return prior.call(window, message, source, lineno, colno, error);
        } catch {
          return false;
        }
      }
      return false;
    };

    window.addEventListener('unhandledrejection', (evt) => {
      try {
        const reason = (evt as PromiseRejectionEvent).reason;
        handleRejection(reason);
      } catch {
        // never throw
      }
    });

    intervalHandle = setInterval(flush, FLUSH_INTERVAL_MS);
  } catch {
    // never throw
  }
}

// --- test-only helpers ------------------------------------------------------
//
// Exposed under an underscore-prefixed name so they're discoverable but not
// part of the public surface. Production callers must use initErrorReporter()
// only. These exist so unit tests can exercise the sanitiser/truncation/queue
// logic without having to dispatch real browser events.

export function _enqueueForTest(raw: RawEntry): void {
  safePush(buildEntry(raw));
}

export function _resetForTest(): void {
  initialised = false;
  activeRealm = 'tenant';
  queue.length = 0;
  if (intervalHandle !== null) {
    clearInterval(intervalHandle);
    intervalHandle = null;
  }
}
